/**
 * Compartilhar a tela: captura e publicação na sala, sem React (o `ScreenShareProvider` só guarda o estado).
 *
 * A captura é pedida ao `navigator` da JANELA DA SALA, a que recebeu o clique, e não ao da guia: com a sala na
 * janela flutuante (outro documento) o `getDisplayMedia` exige gesto do usuário na janela que o chama, e o clique
 * aconteceu na flutuante. Depois disso a trilha é publicada pelo `LocalParticipant` da guia, como o SDK faz em
 * `setScreenShareEnabled` (que só sabe usar o `navigator` global). Parar é o próprio `setScreenShareEnabled(false)`, que
 * despublica a tela e o áudio dela.
 * O botão "Interromper compartilhamento" do navegador encerra a trilha e o SDK a despublica sozinho.
 */
import { LocalAudioTrack, LocalVideoTrack, Track } from 'livekit-client';

const MESSAGE = 'Não foi possível compartilhar a tela. Confira se o navegador tem permissão para gravar a tela e tente de novo.';

const navigatorOf = (win) => win?.navigator ?? (typeof navigator !== 'undefined' ? navigator : null);

/** O navegador sabe capturar a tela? Não no celular (Android e iOS não têm `getDisplayMedia`). */
export function supportsScreenShare(win) {
    return typeof navigatorOf(win)?.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * Opções do `getDisplayMedia`: até Full HD a 30 quadros (sem limite, uma tela 4K seria codificada inteira, e é caro), a guia da
 * própria chamada fora da lista (compartilhá-la faz o vídeo se enxergar em espelho e o som da chamada voltar para quem fala) e,
 * como no Google Meet, o ÁUDIO: o seletor do Chrome ganha "Compartilhar áudio da guia" (e, ao compartilhar a tela toda no
 * Windows, o do sistema); a pessoa marca ou não. Sem tratamento de voz (eco, ruído, ganho): é som de vídeo e de música, não
 * microfone. O Safari não aceita restrições de tamanho na captura de tela e não captura áudio.
 */
export function screenCaptureOptions(nav) {
    const safari = /^((?!chrome|chromium|android).)*safari/i.test(nav?.userAgent ?? '');
    return {
        video: safari ? true : { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        selfBrowserSurface: 'exclude',
        surfaceSwitching: 'include',
        systemAudio: 'include',
    };
}

/**
 * O texto de erro que a pessoa vê, ou `null` quando não é erro: fechar o seletor sem escolher nada (ou negar a
 * permissão) rejeita com `NotAllowedError` e não merece aviso.
 */
export function screenShareErrorMessage(error) {
    return error?.name === 'NotAllowedError' ? null : MESSAGE;
}

/**
 * Pede a captura na janela dada e publica a trilha da tela e, se a pessoa marcou "compartilhar áudio", a do áudio (fonte
 * `ScreenShareAudio`, que o outro lado toca sozinho e o transcritor ignora). Devolve a trilha de vídeo publicada.
 * @param {import('livekit-client').LocalParticipant} localParticipant
 * @param {Window} [win] a janela da sala (a do clique)
 */
export async function startScreenShare(localParticipant, win) {
    const nav = navigatorOf(win);
    if (!supportsScreenShare(win)) throw Object.assign(new Error('getDisplayMedia indisponível'), { name: 'NotSupportedError' });
    const stream = await nav.mediaDevices.getDisplayMedia(screenCaptureOptions(nav));
    const [mediaTrack] = stream.getVideoTracks();
    const [mediaAudio] = stream.getAudioTracks();
    if (!mediaTrack) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error('A captura não trouxe vídeo');
    }
    const video = new LocalVideoTrack(mediaTrack, undefined, false);
    video.source = Track.Source.ScreenShare;
    const tracks = [video];
    if (mediaAudio) {
        const audio = new LocalAudioTrack(mediaAudio, undefined, false);
        audio.source = Track.Source.ScreenShareAudio;
        tracks.push(audio);
    }
    try {
        await Promise.all(tracks.map((track) => localParticipant.publishTrack(track)));
    } catch (error) {
        // Uma trilha parada solta a captura: some o aviso "compartilhando" do navegador.
        tracks.forEach((track) => track.stop());
        throw error;
    }
    return video;
}

/** Para de compartilhar: despublica a trilha, o que também solta a captura (some o aviso do navegador). */
export function stopScreenShare(localParticipant) {
    return localParticipant.setScreenShareEnabled(false);
}
